import { createFileRoute } from "@tanstack/react-router";
import { RotateCcw, Save, Upload } from "lucide-react";
import { useId, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormSkeleton, PageSkeleton, toast } from "@bs/ui";
import { Button } from "@bs/ui";
import { Input } from "@bs/ui";
import { FieldLabel } from "@bs/ui";
import { Field } from "@bs/ui";
import { HeaderActions, SettingsCard, SettingsPageFrame, useUnsavedGuard } from "../../../components/settings/settings-page.tsx";
import { SimpleSelect } from "@bs/ui";
import type { BrandSettings } from "@bs/contracts";
import { orpc } from "../../../lib/orpc.ts";
import { MediaField } from "@bs/content-admin/media/media-field.tsx";

const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const curatedFonts = [
  { id: "Inter", name: "Inter", category: "Sans-serif", devanagari: false },
  { id: "Plus Jakarta Sans", name: "Plus Jakarta Sans", category: "Sans-serif", devanagari: false },
  { id: "DM Sans", name: "DM Sans", category: "Sans-serif", devanagari: false },
  { id: "Poppins", name: "Poppins", category: "Geometric Sans", devanagari: true },
  { id: "Mukta", name: "Mukta", category: "Humanist Sans", devanagari: true },
  { id: "Rozha One", name: "Rozha One", category: "Display Serif", devanagari: true },
];

const colorFields = [
  { key: "primaryColor", label: "Primary color" },
  { key: "secondaryColor", label: "Secondary color" },
  { key: "accentColor", label: "Accent color" },
  { key: "backgroundColor", label: "Background color" },
  { key: "surfaceColor", label: "Surface color" },
  { key: "textColor", label: "Text color" },
] as const;

type ColorKey = (typeof colorFields)[number]["key"];

const IMAGE_SLOTS = [
  { key: "logoLightMediaId", label: "Logo (light background)", aspect: undefined },
  { key: "logoDarkMediaId", label: "Logo (dark background)", aspect: undefined },
  { key: "faviconMediaId", label: "Favicon", aspect: "1:1" as const },
  { key: "appleTouchIconMediaId", label: "Apple Touch Icon", aspect: "1:1" as const },
  { key: "socialImageMediaId", label: "Social sharing image", aspect: "1.91:1" as const },
] as const;

interface FormState {
  storeName: string;
  logoLightMediaId: string | null;
  logoDarkMediaId: string | null;
  faviconMediaId: string | null;
  appleTouchIconMediaId: string | null;
  socialImageMediaId: string | null;
  logoWidth: number;
  fontHeading: BrandSettings["fontHeading"];
  fontBody: BrandSettings["fontBody"];
  fontSizeScale: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  surfaceColor: string;
  textColor: string;
  colorMode: BrandSettings["colorMode"];
  cornerRadius: BrandSettings["cornerRadius"];
  buttonStyle: BrandSettings["buttonStyle"];
}

function fromServer(b: BrandSettings, storeName: string): FormState {
  return {
    storeName,
    logoLightMediaId: b.logoLightMediaId ?? null,
    logoDarkMediaId: b.logoDarkMediaId ?? null,
    faviconMediaId: b.faviconMediaId ?? null,
    appleTouchIconMediaId: b.appleTouchIconMediaId ?? null,
    socialImageMediaId: b.socialImageMediaId ?? null,
    logoWidth: b.logoWidth,
    fontHeading: b.fontHeading,
    fontBody: b.fontBody,
    fontSizeScale: b.fontSizeScale,
    primaryColor: b.primaryColor,
    secondaryColor: b.secondaryColor,
    accentColor: b.accentColor,
    backgroundColor: b.backgroundColor,
    surfaceColor: b.surfaceColor,
    textColor: b.textColor,
    colorMode: b.colorMode,
    cornerRadius: b.cornerRadius,
    buttonStyle: b.buttonStyle,
  };
}

function BrandingLoading() {
  return (
    <SettingsPageFrame title="Branding" description="Logos, colours and typography.">
      <SettingsCard>
        <FormSkeleton fields={6} />
      </SettingsCard>
    </SettingsPageFrame>
  );
}

export const Route = createFileRoute("/_store/settings/branding")({
  pendingComponent: () => <PageSkeleton />,
  component: BrandingSettingsPage,
});

export function BrandingSettingsPage() {
  const brandQuery = useQuery(orpc.admin.branding.get.queryOptions());
  const settingsQuery = useQuery(orpc.admin.settings.get.queryOptions());

  const failed = brandQuery.isError ? brandQuery : settingsQuery.isError ? settingsQuery : null;
  if (failed) {
    return (
      <SettingsPageFrame title="Branding" description="Logos, colours and typography.">
        <SettingsCard>
          <div role="alert" className="flex flex-col items-start gap-3 rounded-md border border-destructive/40 bg-destructive/5 p-4">
            <p className="text-xs font-medium text-foreground">Could not load branding settings</p>
            <p className="text-xs text-muted-foreground">{failed.error?.message}</p>
            <Button
              size="sm"
              onClick={() => {
                void brandQuery.refetch();
                void settingsQuery.refetch();
              }}
            >
              <RotateCcw className="size-3.5" aria-hidden />
              Retry
            </Button>
          </div>
        </SettingsCard>
      </SettingsPageFrame>
    );
  }
  if (!brandQuery.data || !settingsQuery.data) return <BrandingLoading />;

  return (
    <BrandingEditor
      key={`${brandQuery.data.id}:${brandQuery.data.version}`}
      brand={brandQuery.data}
      storeName={settingsQuery.data.storeName}
    />
  );
}

function BrandingEditor({ brand, storeName }: { brand: BrandSettings; storeName: string }) {
  const queryClient = useQueryClient();
  const baseline = fromServer(brand, storeName);
  const [form, setForm] = useState<FormState>(baseline);
  const nameId = useId();

  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);
  const guard = useUnsavedGuard(dirty);
  const invalidColors = colorFields.filter((f) => !HEX.test(form[f.key]));

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((p) => ({ ...p, [key]: value }));

  const invalidateAll = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: orpc.admin.branding.get.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.admin.settings.get.key() }),
    ]);

  const updateBrand = useMutation(orpc.admin.branding.update.mutationOptions());
  const updateSettings = useMutation(orpc.admin.settings.update.mutationOptions());
  const publishBrand = useMutation(orpc.admin.branding.publish.mutationOptions());

  const validate = (): string | null => {
    if (!form.storeName.trim()) return "Store name is required";
    if (invalidColors.length > 0) return `Enter valid hex colours for: ${invalidColors.map((f) => f.label).join(", ")}`;
    if (!Number.isInteger(form.logoWidth) || form.logoWidth < 20 || form.logoWidth > 1000) return "Logo width must be between 20 and 1000 px";
    return null;
  };

  const persist = async (): Promise<boolean> => {
    const problem = validate();
    if (problem) {
      toast.error(problem);
      return false;
    }
    const { storeName: name, ...brandFields } = form;
    try {
      if (name.trim() !== baseline.storeName) await updateSettings.mutateAsync({ storeName: name.trim() });
      await updateBrand.mutateAsync(brandFields);
      return true;
    } catch (err) {
      toast.error(`Could not save branding: ${err instanceof Error ? err.message : "unknown error"}`);
      return false;
    }
  };

  const handleSave = async () => {
    if (await persist()) {
      await invalidateAll();
      toast.success("Branding saved.");
    }
  };

  const handlePublish = async () => {
    if (dirty && !(await persist())) return;
    try {
      const res = await publishBrand.mutateAsync(undefined);
      await invalidateAll();
      toast.success(`Branding published (version ${res.version}).`);
    } catch (err) {
      toast.error(`Could not publish branding: ${err instanceof Error ? err.message : "unknown error"}`);
    }
  };

  const busy = updateBrand.isPending || updateSettings.isPending || publishBrand.isPending;

  return (
    <SettingsPageFrame
      title="Branding & visual identity"
      description={`Logos, colours and typography. Published version ${brand.version}${brand.publishedAt ? `, last published ${new Date(brand.publishedAt).toLocaleDateString()}` : ", not published yet"}.`}
    >
      {guard}
      <HeaderActions>
        <Button variant="outline" disabled={!dirty || busy} onClick={() => setForm(baseline)}>
          <RotateCcw className="mr-1.5 size-3.5" aria-hidden />
          Discard
        </Button>
        <Button variant="outline" disabled={!dirty || busy} onClick={() => void handleSave()}>
          <Save className="mr-1.5 size-3.5" aria-hidden />
          {updateBrand.isPending || updateSettings.isPending ? "Saving…" : "Save branding"}
        </Button>
        <Button disabled={busy} onClick={() => void handlePublish()}>
          <Upload className="mr-1.5 size-3.5" aria-hidden />
          {publishBrand.isPending ? "Publishing…" : "Publish"}
        </Button>
      </HeaderActions>
          <SettingsCard
            title="Store identity & images"
            description="Logos, favicon & store display name"
          >
            <div className="space-y-4">
              <div className="grid gap-1.5">
                <FieldLabel htmlFor={nameId}>Store name</FieldLabel>
                <Input id={nameId} value={form.storeName} aria-invalid={!form.storeName.trim()} onChange={(e) => set("storeName", e.target.value)} required />
                {form.storeName.trim() ? null : <p className="text-xs text-destructive">Store name is required</p>}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {IMAGE_SLOTS.map((slot) => (
                  <MediaField
                    key={slot.key}
                    value={form[slot.key]}
                    onChange={(id) => set(slot.key, id)}
                    accept={["image"]}
                    label={slot.label}
                    aspect={slot.aspect}
                  />
                ))}
              </div>

              <div className="grid gap-1.5">
                <FieldLabel htmlFor="logo-width">Logo width (px)</FieldLabel>
                <Input
                  id="logo-width"
                  type="number"
                  min={20}
                  max={1000}
                  value={form.logoWidth}
                  onChange={(e) => set("logoWidth", Number(e.target.value))}
                />
              </div>
            </div>
          </SettingsCard>

          <SettingsCard
            title="Brand colours"
            description="Hex palette & button geometry"
          >
            <div className="grid gap-3 sm:grid-cols-2">
              {colorFields.map((f) => {
                const value = form[f.key as ColorKey];
                const valid = HEX.test(value);
                return (
                  <div key={f.key} className="grid gap-1.5">
                    <FieldLabel htmlFor={f.key}>{f.label}</FieldLabel>
                    <div className="flex items-center gap-2">
                      <input
                        id={f.key}
                        type="color"
                        value={valid && value.length === 7 ? value : "#000000"}
                        onChange={(e) => set(f.key, e.target.value)}
                        className="size-8 cursor-pointer rounded-md border border-border p-0.5 bg-background shrink-0"
                      />
                      <Input aria-label={`${f.label} hex value`} value={value} aria-invalid={!valid} onChange={(e) => set(f.key, e.target.value)} className="font-mono h-8 text-xs" />
                    </div>
                    {valid ? null : <p className="text-xs text-destructive">Use a hex colour like #1a2b3c</p>}
                  </div>
                );
              })}
            </div>
            <div className="pt-2 border-t border-border grid gap-3 sm:grid-cols-3">
              <Field id="color-mode" label="Colour mode">
                <SimpleSelect
                  id="color-mode"
                  className="w-full"
                  value={form.colorMode}
                  onChange={(v) => set("colorMode", v as FormState["colorMode"])}
                  options={[
                    { value: "light", label: "Light" },
                    { value: "dark", label: "Dark" },
                    { value: "auto", label: "Match device" },
                  ]}
                />
              </Field>
              <Field id="corner-radius" label="Corner radius">
                <SimpleSelect
                  id="corner-radius"
                  className="w-full"
                  value={form.cornerRadius}
                  onChange={(v) => set("cornerRadius", v as FormState["cornerRadius"])}
                  options={(["none", "small", "medium", "large", "full"] as const).map((r) => ({ value: r, label: r }))}
                />
              </Field>
              <Field id="button-style" label="Button style">
                <SimpleSelect
                  id="button-style"
                  className="w-full"
                  value={form.buttonStyle}
                  onChange={(v) => set("buttonStyle", v as FormState["buttonStyle"])}
                  options={(["solid", "outline", "pill"] as const).map((r) => ({ value: r, label: r }))}
                />
              </Field>
            </div>
          </SettingsCard>

          <SettingsCard
            title="Typography"
            description="Font families & text sizing scale"
          >
            <div className="grid gap-3 sm:grid-cols-3">
              {(
                [
                  { key: "fontHeading", label: "Heading font" },
                  { key: "fontBody", label: "Body font" },
                ] as const
              ).map((f) => (
                <Field key={f.key} id={f.key} label={f.label}>
                  <SimpleSelect
                    id={f.key}
                    value={form[f.key]}
                    onChange={(v) => set(f.key, v as BrandSettings[typeof f.key])}
                    options={[
                      ...(curatedFonts.some((c) => c.id === form[f.key]) ? [] : [{ value: form[f.key], label: form[f.key] }]),
                      ...curatedFonts.map((c) => ({ value: c.id, label: `${c.name} (${c.category})${c.devanagari ? " - Devanagari" : ""}` })),
                    ]}
                  />
                </Field>
              ))}
              <Field id="font-scale" label="Text size">
                <SimpleSelect
                  id="font-scale"
                  value={form.fontSizeScale}
                  onChange={(v) => set("fontSizeScale", v)}
                  options={[
                    ...(["small", "default", "large"].includes(form.fontSizeScale) ? [] : [{ value: form.fontSizeScale, label: form.fontSizeScale }]),
                    { value: "small", label: "Small" },
                    { value: "default", label: "Default" },
                    { value: "large", label: "Large" },
                  ]}
                />
              </Field>
            </div>
          </SettingsCard>
    </SettingsPageFrame>
  );
}
